import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  ArrowBack,
  CalendarToday,
  Delete,
  EmojiEvents,
  LocationOn,
  People,
  Share,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  useAddTournamentFavorite,
  useDeleteTournament,
  useMeFavorites,
  useRemoveTournamentFavorite,
  useTournament,
  useTournamentBracket,
  useTournamentMatches,
  useTournamentRegistrations,
  useTournamentResults,
  useTournamentStandings,
  useVenue,
} from '@/hooks/useApi';
import { useAdminMode, useTournamentWorkflow } from '@/hooks/useManage';
import { useAuthStore, useFavoritesStore } from '@/store';
import { ErrorState, LoadingState, MatchCard, StandingsTable } from '@/components';
import {
  ActionButton,
  ActionDialog,
  actionNeedsDialog,
  KebabMenu,
  shareLink,
  useTournamentActionRunner,
  WorkflowView,
  type KebabItem,
  type RunAction,
} from '@/components/manage';
import type { Action, TournamentTabsItem } from '@/api/generated/model';
import type { Match, Player, StandingEntry, Team, Tournament } from '@/types';
import { useNotification } from '@/contexts/NotificationContext';
import { getApiErrorMessage } from '@/utils/apiError';
import { loginPath } from '@/utils/loginRedirect';

const TAB_LABELS: Record<TournamentTabsItem, string> = {
  overview: 'Обзор',
  workflow: 'Управление',
  matches: 'Матчи',
  standings: 'Таблица',
  participants: 'Участники',
  signups: 'Запись',
  bracket: 'Сетка',
  results: 'Итоги',
};

const DEFAULT_TABS: TournamentTabsItem[] = ['overview', 'matches', 'standings', 'participants'];

const SPORT_ICONS: Record<string, string> = {
  football: '⚽', hockey: '🏒', basketball: '🏀', tennis: '🎾', volleyball: '🏐',
  handball: '🤾', padel: '🎾', swimming: '🏊', poker: '🃏', other: '🏆',
};

const STATUS: Record<string, { label: string; color: 'default' | 'success' | 'info' | 'warning' | 'error' }> = {
  draft: { label: 'Черновик', color: 'default' },
  published: { label: 'Открыт', color: 'info' },
  ongoing: { label: 'Идёт', color: 'success' },
  completed: { label: 'Завершён', color: 'warning' },
  canceled: { label: 'Отменён', color: 'error' },
  archived: { label: 'В архиве', color: 'default' },
};

const participantLabel = (p: Team | Player): string =>
  'firstName' in p ? `${p.firstName} ${p.lastName || ''}`.trim() : p.name;

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : null;

const VenueLabel: React.FC<{ venueId: string }> = ({ venueId }) => {
  const { data } = useVenue(venueId);
  return <>{data?.name ?? 'Площадка'}</>;
};

type ApiStandingRow = {
  participantId?: string;
  played?: number;
  won?: number;
  draw?: number;
  lost?: number;
  goalsFor?: number | null;
  goalsAgainst?: number | null;
  points?: number;
};

const toEntries = (standings: unknown, nameOf: (id: string) => string): StandingEntry[] => {
  if (!standings) return [];
  const s = standings as { rows?: ApiStandingRow[]; entries?: StandingEntry[] };
  if (s.entries?.length) return s.entries;
  return (s.rows ?? []).map((row, ix) => {
    const gf = row.goalsFor ?? 0;
    const ga = row.goalsAgainst ?? 0;
    return {
      position: ix + 1,
      participantId: row.participantId || '',
      participantName: nameOf(row.participantId || ''),
      played: row.played ?? 0,
      won: row.won ?? 0,
      drawn: row.draw ?? 0,
      lost: row.lost ?? 0,
      goalsFor: gf,
      goalsAgainst: ga,
      goalsDiff: gf - ga,
      points: row.points ?? 0,
    };
  });
};

const groupByRound = (matches: Match[]) => {
  const sorted = [...matches].sort((a, b) => {
    const at = a.scheduledAt ? Date.parse(a.scheduledAt) : Number.POSITIVE_INFINITY;
    const bt = b.scheduledAt ? Date.parse(b.scheduledAt) : Number.POSITIVE_INFINITY;
    return at !== bt ? at - bt : a.id.localeCompare(b.id);
  });
  const groups = new Map<string, { label: string; matches: Match[] }>();
  for (const m of sorted) {
    const key = m.round != null ? `r${m.round}` : 'none';
    if (!groups.has(key)) groups.set(key, { label: m.round != null ? `Тур ${m.round}` : 'Матчи', matches: [] });
    groups.get(key)!.matches.push(m);
  }
  return Array.from(groups.values());
};

const EmptyBlock: React.FC<{ text: string; action?: React.ReactNode }> = ({ text, action }) => (
  <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 0 }}>
    <Typography color="text.secondary">{text}</Typography>
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Paper>
);

const SignupSummary: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  const s = tournament.signup;
  if (!s) return null;
  const max = s.maxParticipants ? ` из ${s.maxParticipants}` : '';
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body1">
        Записались: <b>{s.going ?? 0}{max}</b>
        {s.waitlist ? ` · лист ожидания ${s.waitlist}` : ''}
      </Typography>
      {s.viewerStatus === 'going' && <Chip size="small" color="success" label="Вы записаны" />}
      {s.viewerStatus === 'waitlist' && <Chip size="small" color="warning" label="Вы в листе ожидания" />}
    </Box>
  );
};

export const TournamentDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { adminMode, can } = useAdminMode();
  const { showSuccess, showError } = useNotification();
  const [menuAction, setMenuAction] = useState<Action | null>(null);

  const { data: tournament, isLoading, error, refetch } = useTournament(id);
  const permissions = tournament?.permissions;
  const canWorkflow = can(permissions?.manageWorkflow);
  const { data: workflow } = useTournamentWorkflow(id, canWorkflow);
  const runTournamentAction = useTournamentActionRunner(id);

  const serverTabs = tournament?.tabs?.length ? tournament.tabs : DEFAULT_TABS;
  const tabs = serverTabs.filter((t) => t !== 'workflow' || (canWorkflow && !isDesktop));
  if (canWorkflow && !isDesktop && !tabs.includes('workflow')) tabs.unshift('workflow');
  const requested = searchParams.get('tab') === 'manage' ? 'workflow' : searchParams.get('tab');
  const activeTab = (tabs.find((t) => t === requested) ?? tabs[0]) as TournamentTabsItem;
  const setTab = (tab: TournamentTabsItem) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const { data: registrations } = useTournamentRegistrations(id);
  const { data: matches } = useTournamentMatches(id, { limit: 100 });
  const { data: standings } = useTournamentStandings(id, {
    queryKey: ['tournaments', id, 'standings'],
    enabled: Boolean(id) && (activeTab === 'standings' || activeTab === 'results'),
  });
  const { data: bracket } = useTournamentBracket(id, {
    queryKey: ['tournaments', id, 'bracket'],
    enabled: Boolean(id) && activeTab === 'bracket',
  });
  const { data: results } = useTournamentResults(id, { enabled: Boolean(id) && tournament?.status === 'completed' });

  const { isTournamentFavorite, addTournament, removeTournament } = useFavoritesStore();
  const { data: apiFavorites } = useMeFavorites({ enabled: isAuthenticated });
  const addFavorite = useAddTournamentFavorite();
  const removeFavorite = useRemoveTournamentFavorite();
  const deleteTournament = useDeleteTournament();

  const nameOf = useMemo(() => {
    const byId = new Map<string, string>();
    registrations?.data.forEach((r) => byId.set(r.participant.id, participantLabel(r.participant)));
    matches?.data.forEach((m) => {
      if (m.homeId && m.homeName) byId.set(m.homeId, m.homeName);
      if (m.awayId && m.awayName) byId.set(m.awayId, m.awayName);
    });
    return (pid: string) => byId.get(pid) ?? 'Участник';
  }, [registrations, matches]);

  const standingsEntries = useMemo(() => toEntries(standings, nameOf), [standings, nameOf]);

  if (isLoading) return <LoadingState fullscreen message="Загрузка турнира..." />;
  if (error || !tournament) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton aria-label="Назад" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>
        <ErrorState message="Ошибка загрузки турнира" onRetry={refetch} />
      </Box>
    );
  }

  const isFavorite = isAuthenticated
    ? Boolean(apiFavorites?.tournaments?.some((item) => (item as { id: string }).id === id))
    : isTournamentFavorite(id);
  const toggleFavorite = () => {
    if (isAuthenticated) (isFavorite ? removeFavorite : addFavorite).mutate(id);
    else (isFavorite ? removeTournament : addTournament)(id);
  };

  /** Действия зрителя требуют входа. */
  const runViewerAction: RunAction = async (action, values) => {
    if (!isAuthenticated) {
      navigate(loginPath(`${location.pathname}${location.search}`));
      return;
    }
    return runTournamentAction(action, values);
  };

  const runMenuAction = (action: Action) => {
    if (action.href) navigate(action.href);
    else if (actionNeedsDialog(action)) setMenuAction(action);
    else
      void runTournamentAction(action, action.params).catch((err) => showError(getApiErrorMessage(err)));
  };

  const shareTarget = { title: tournament.name, url: `${window.location.origin}/tournaments/${tournament.id}` };

  const kebabItems: KebabItem[] = [
    {
      id: 'share',
      label: 'Поделиться',
      icon: <Share fontSize="small" />,
      onClick: async () => {
        try {
          if ((await shareLink(tournament.name, shareTarget.url)) === 'copied') showSuccess('Ссылка скопирована');
        } catch {
          /* пользователь закрыл системное меню */
        }
      },
    },
    ...(canWorkflow ? workflow?.menuActions ?? [] : [])
      .filter((a) => a.enabled)
      .map<KebabItem>((a) => ({ id: a.id, label: a.label, danger: a.style === 'danger', onClick: () => runMenuAction(a) })),
    {
      id: 'edit',
      label: 'Изменить',
      hidden: !can(permissions?.update) || Boolean(workflow?.menuActions?.some((a) => a.id === 'edit_settings')),
      onClick: () => navigate(`/tournaments/${id}/settings`),
    },
    {
      id: 'delete',
      label: 'Удалить',
      icon: <Delete fontSize="small" />,
      danger: true,
      hidden: !can(permissions?.delete),
      confirm: { title: 'Удалить турнир?', text: 'Турнир пропадёт из списков. Восстановить может администратор.' },
      onClick: async () => {
        try {
          await deleteTournament.mutateAsync(id);
          navigate('/tournaments');
        } catch (err) {
          showError(getApiErrorMessage(err, 'Не удалось удалить турнир.'));
        }
      },
    },
  ];

  const status = STATUS[tournament.status] ?? STATUS.draft;
  const championId = results?.podium?.championId;
  const matchList = matches?.data ?? [];
  const participantMode = (tournament.settings as { participantMode?: string } | undefined)?.participantMode;
  const canAddMatch = can(permissions?.update) && participantMode !== 'pickup';
  const approved = registrations?.data.filter((r) => r.status === 'approved') ?? [];
  const viewerActions = tournament.viewerActions ?? [];

  const tabContent = (() => {
    switch (activeTab) {
      case 'workflow':
        return workflow ? (
          <Box sx={{ p: 1 }}>
            <WorkflowView workflow={workflow} run={runTournamentAction} sportType={tournament.sportType} share={shareTarget} />
          </Box>
        ) : (
          <LoadingState message="Загрузка сценария..." />
        );
      case 'overview':
        return (
          <Stack spacing={2} sx={{ p: 2 }}>
            {tournament.status === 'completed' && championId && (
              <Alert severity="success" icon={<EmojiEvents />}>
                Победитель: <b>{nameOf(championId)}</b>
              </Alert>
            )}
            <SignupSummary tournament={tournament} />
            <Typography color="text.secondary">
              {approved.length > 0 ? `Участников: ${approved.length}` : 'Участники ещё не определены'}
              {matchList.length > 0 ? ` · матчей: ${matchList.length}` : ''}
            </Typography>
            {matchList.some((m) => m.status === 'live') && (
              <Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Сейчас идут
                </Typography>
                {matchList
                  .filter((m) => m.status === 'live')
                  .map((m) => (
                    <MatchCard key={m.id} match={m} compact resolveParticipantName={nameOf} onClick={() => navigate(`/matches/${m.id}`)} />
                  ))}
              </Box>
            )}
          </Stack>
        );
      case 'matches':
        return matchList.length === 0 ? (
          <EmptyBlock
            text="Матчей пока нет"
            action={
              canAddMatch && (
                <Button variant="contained" color="manage" startIcon={<Add />} onClick={() => navigate(`/tournaments/${id}/matches/create`)}>
                  Добавить матч
                </Button>
              )
            }
          />
        ) : (
          <Box>
            {groupByRound(matchList).map((group) => (
              <Box key={group.label}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1.5, py: 0.75 }}>
                  {group.label}
                </Typography>
                {group.matches.map((m) => (
                  <MatchCard key={m.id} match={m} compact resolveParticipantName={nameOf} onClick={() => navigate(`/matches/${m.id}`)} />
                ))}
              </Box>
            ))}
            {canAddMatch && (
              <Box sx={{ p: 2 }}>
                <Button variant="outlined" color="manage" startIcon={<Add />} onClick={() => navigate(`/tournaments/${id}/matches/create`)}>
                  Добавить матч
                </Button>
              </Box>
            )}
          </Box>
        );
      case 'standings':
        return standingsEntries.length === 0 ? (
          <EmptyBlock text="Таблица появится после первых матчей" />
        ) : (
          <StandingsTable
            entries={standingsEntries}
            onRowClick={(e) => navigate(participantMode === 'pickup' ? `/tournaments/${id}?tab=matches` : `/teams/${e.participantId}`)}
          />
        );
      case 'participants':
        return approved.length === 0 ? (
          <EmptyBlock text="Участников пока нет" />
        ) : (
          <List disablePadding>
            {approved.map((r) => (
              <ListItemButton
                key={r.id}
                sx={{ minHeight: 48, borderBottom: 1, borderColor: 'divider' }}
                onClick={() => navigate('firstName' in r.participant ? `/players/${r.participant.id}` : `/teams/${r.participant.id}`)}
              >
                <ListItemText primary={participantLabel(r.participant)} />
              </ListItemButton>
            ))}
          </List>
        );
      case 'signups':
        return (
          <Stack spacing={2} sx={{ p: 2 }}>
            <SignupSummary tournament={tournament} />
            {viewerActions.map((a) => (
              <ActionButton key={a.id} action={a} run={runViewerAction} fullWidth />
            ))}
          </Stack>
        );
      case 'bracket':
        return bracket?.rounds?.length ? (
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', p: 1.5 }}>
            {bracket.rounds.map((round) => (
              <Box key={round.round} sx={{ minWidth: 200 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {round.name}
                </Typography>
                <Stack spacing={1}>
                  {round.matches.map((m) => (
                    <Paper key={m.id} variant="outlined" sx={{ p: 1, cursor: 'pointer' }} onClick={() => navigate(`/matches/${m.id}`)}>
                      <Typography variant="body2">
                        {m.homeName ?? '—'} <b>{m.homeScore ?? ''}</b>
                      </Typography>
                      <Typography variant="body2">
                        {m.awayName ?? '—'} <b>{m.awayScore ?? ''}</b>
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        ) : (
          <EmptyBlock text="Сетка появится после формирования расписания" />
        );
      case 'results':
        return (
          <Stack spacing={2} sx={{ p: 2 }}>
            {championId ? (
              <Alert severity="success" icon={<EmojiEvents />}>
                Победитель: <b>{nameOf(championId)}</b>
                {results?.podium?.runnerUpId && <> · 2 место: {nameOf(results.podium.runnerUpId)}</>}
              </Alert>
            ) : (
              <Typography color="text.secondary">Итоги появятся после завершения</Typography>
            )}
            {standingsEntries.length > 0 && <StandingsTable entries={standingsEntries} />}
          </Stack>
        );
      default:
        return null;
    }
  })();

  const nextStep = canWorkflow ? workflow?.nextStep : undefined;

  const main = (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'background.default', fontSize: '1.6rem' }}>
            {SPORT_ICONS[tournament.sportType] ?? '🏆'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h2" component="h1" sx={{ wordBreak: 'break-word' }}>
              {tournament.name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              <Chip size="small" label={status.label} color={status.color} />
              {tournament.cityName && <Chip size="small" label={tournament.cityName} />}
            </Box>
          </Box>
          <IconButton aria-label={isFavorite ? 'Убрать из избранного' : 'В избранное'} onClick={toggleFavorite} sx={{ width: 48, height: 48 }}>
            {isFavorite ? <Star color="warning" /> : <StarBorder />}
          </IconButton>
          <KebabMenu items={kebabItems} />
        </Box>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 1.5, color: 'text.secondary' }}>
          {tournament.startDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday fontSize="small" />
              <Typography variant="body2">{formatDateTime(tournament.startDate)}</Typography>
            </Box>
          )}
          {tournament.venueIds?.[0] && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn fontSize="small" />
              <Typography variant="body2">
                <VenueLabel venueId={tournament.venueIds[0]} />
              </Typography>
            </Box>
          )}
          {approved.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <People fontSize="small" />
              <Typography variant="body2">{approved.length}</Typography>
            </Box>
          )}
        </Stack>
        {viewerActions.length > 0 && activeTab !== 'signups' && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {viewerActions.map((a) => (
              <ActionButton key={a.id} action={a} run={runViewerAction} fullWidth />
            ))}
          </Stack>
        )}
        {nextStep && (
          <Paper
            variant="outlined"
            sx={{ mt: 2, p: 1.5, borderColor: 'manage.main', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: 'inherit' }}
          >
            <Typography sx={{ flex: 1, minWidth: 160 }}>
              <Typography component="span" color="manage.main" fontWeight={700}>
                Следующий шаг:
              </Typography>{' '}
              {nextStep.title}
            </Typography>
            {nextStep.action ? (
              <ActionButton action={nextStep.action} run={runTournamentAction} size="medium" showHint={false} sportType={tournament.sportType} />
            ) : (
              !isDesktop && (
                <Button color="manage" variant="outlined" onClick={() => setTab('workflow')}>
                  Открыть
                </Button>
              )
            )}
          </Paper>
        )}
      </Box>
      <Tabs value={activeTab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderTop: 1, borderColor: 'divider' }}>
        {tabs.map((t) => (
          <Tab
            key={t}
            value={t}
            label={TAB_LABELS[t]}
            sx={{ minHeight: 48, ...(t === 'workflow' && { color: 'manage.main', '&.Mui-selected': { color: 'manage.main' } }) }}
          />
        ))}
      </Tabs>
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>{tabContent}</Box>
    </Paper>
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2 }}>
      {isDesktop && canWorkflow ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 400px', gap: 2, alignItems: 'start' }}>
          {main}
          <Paper sx={{ p: 1.5, position: 'sticky', top: 72, maxHeight: 'calc(100vh - 88px)', overflowY: 'auto' }}>
            <Typography variant="h4" color="manage.main" sx={{ mb: 1, px: 1 }}>
              Управление
            </Typography>
            {workflow ? (
              <WorkflowView workflow={workflow} run={runTournamentAction} sportType={tournament.sportType} share={shareTarget} />
            ) : (
              <LoadingState message="Загрузка сценария..." />
            )}
          </Paper>
        </Box>
      ) : (
        main
      )}
      <ActionDialog
        action={menuAction}
        run={runTournamentAction}
        onClose={() => setMenuAction(null)}
        sportType={tournament.sportType}
      />
      {adminMode && !canWorkflow && permissions && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          У вас нет прав на управление этим турниром.
        </Typography>
      )}
    </Box>
  );
};
